import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Track } from '@/world/Track';
import { tuning, vehicleBoxFor } from '@/config/tuning';
import { forwardX, forwardZ } from '@/math/orientation';
import { carReach, netDepthAt, toGoalCoords, type GoalCfg, type GoalFrame } from '@/physics/goalNet';
import { withBrandLogo } from './finishPlaza';

/**
 * scenery/finishGoal.ts — THE FINISH GOAL (M157, owner: "the finish line will
 * be the balls actually going into a soccer goal… the full width of the course
 * … replace the current arch structure just as a goal. Add a net that catches
 * the balls and cars"). Owner picks: white posts with gold touches, a plain
 * WHITE goal line (no checker), and in the six landmark cities the goal is
 * built INTO the landmark (it stands at the line under Tower Bridge, inside
 * the Galleria hall, through the Gateway of India arch…).
 *
 * Every city gets the same goal, standing ON the finish line:
 *  - white round posts on the rail line (inner faces exactly post to post =
 *    the road) and a crossbar `tuning.goal.height` up, gold collars just
 *    above the guardrail (the rail hides the posts' true feet);
 *  - the classic frame behind: top bars back to a shallow roof, stanchions
 *    slanting down to the deep foot of the net, a ground bar;
 *  - the NET — roof, slanted back, two sides — a mesh texture on panels whose
 *    vertices BULGE around any ball or car pushing into it. The bulge reads
 *    the same synced positions the physics (physics/goalNet.ts) acts on, so
 *    what you see stretching is exactly what is being caught, on every
 *    client, with nothing sent over the wire;
 *  - a black-and-gold DRIBBLE RACER board on the turf behind the net, past
 *    the furthest the net can ever stretch.
 *
 * The geometry is built in GOAL coordinates (s into the goal, lat across, h
 * up from the goal line's road) and mapped to the world through the track's
 * goal frame, with the turf heights sampled from the real road so the frame
 * stands on a hill exactly as the road does.
 *
 * `buildGoal` builds a goal from a frame + a `tuning.goal`-shaped config, so
 * the How to Play intro clip (ui/howto/IntroClip.ts) raises the SAME goal at
 * its own smaller scale and presses its scripted bodies into the same net.
 * Only `buildFinishGoal` (the race world) registers the per-frame race net.
 */

/** The board behind the net: how far past the net's furthest stretch. */
const BOARD_GAP = 1.4;
const BOARD_H = 2.4;
/** Net mesh: world size of one repeating hex tile (3 hex sides across,
 *  √3 sides up — a hex side of 0.5, a real 12 cm net mesh at ×4.9 scale). */
const NET_HEX_W = 1.5;
const NET_HEX_H = NET_HEX_W / Math.sqrt(3);
/** Vertex spacing of the moving panels: fine enough that a bulge is a smooth
 *  dome, not facets (0.8 drew jagged, "shattered" hex lines — reviewed). */
const NET_GRID = 0.5;
/** How hard the far-away net veil is thinned (alpha^this; 1 = untouched). */
const NET_ALPHA_POW = 1.7;
/** Race side nets start on top of the guardrail (TrackMesh wallH). */
const RAIL_TOP = 1.4;
/** Visual slack: the rest shape hangs a touch outward between the bars. */
const ROOF_SAG = 0.35;
const BACK_SLACK = 0.3;
/** How close a body may come to the net before the mesh starts to wrap it. */
const WRAP = 0.3;
/** How far the net is drawn in around a body pressing into it (the skirt). */
const SKIRT = 2.6;
/** The ripple a hit sends across the net: wave number, speed, decay. */
const RIPPLE_K = 1.15;
const RIPPLE_W = 11;
const RIPPLE_FRONT = 18;
const RIPPLE_DECAY = 2.6;
const RIPPLE_LIFE = 1.6;
const RIPPLE_MAX = 6;

/** The minimal racer surface the race net reads (SimRacer satisfies it). */
export interface GoalNetRacer {
  vehicleId: string;
  car: {
    state: { x: number; z: number };
    snapshot(alpha: number): { x: number; y: number; z: number; heading: number; speed: number };
  };
  ball: {
    state: { x: number; z: number; radius: number };
    snapshot(alpha: number): { x: number; y: number; z: number; vx: number; vz: number };
  };
}

/** A body that may press on the net this frame (world position). */
export interface GoalNetBody {
  /** Identity across frames (the hit edge: one swish + ripple per bite). */
  key: object;
  x: number;
  y: number; // the height the body meets the back net at (its centre)
  z: number;
  reach: number; // how far its surface sticks out toward the back net
  up: number; // how far its top sits above `y` (the roof)
  coreLat: number; // the body's own footprint on the net: fully pushed
  coreH: number;
  vInto: number; // speed into the goal (sizes the ripple + swish)
}

/** Converted to goal coordinates. */
interface Press {
  s: number;
  lat: number;
  h: number;
  reach: number;
  up: number;
  coreLat: number;
  coreH: number;
}

/** A hit travelling across the back net as a fading ring. */
interface Ripple {
  lat: number;
  h: number;
  t0: number;
  amp: number;
}

/** A deformable net panel: rest positions + outward normals in goal coords. */
interface Panel {
  mesh: THREE.Mesh;
  pos: THREE.BufferAttribute;
  rest: Float32Array; // s, lat, h per vertex
  normal: Float32Array; // ns, nlat, nh per vertex (outward)
  pin: Float32Array; // 0..1 how free each vertex is to move
  kind: 'back' | 'roof';
  bulged: boolean;
}

/** The live RACE net of the world currently on screen (one race world at a
 *  time; the intro clip's goal keeps its own and never registers here). */
let activeNet: GoalNet | null = null;

/** Hook for the net's swish when a ball or car hits it (Game wires the sfx). */
let onSwish: ((intensity: number) => void) | null = null;
export function setGoalNetSwish(cb: ((intensity: number) => void) | null): void {
  onSwish = cb;
}

const raceBodies: GoalNetBody[] = [];

/** Per rendered frame (Game.renderRace): let the race net wrap whatever is in
 *  it. `still` (reduced motion) keeps the wrap but drops the travelling ripple. */
export function tickGoalNet(racers: readonly GoalNetRacer[], alpha: number, still = false): void {
  const net = activeNet;
  if (!net) return;
  const f = net.frame;
  const cfg = net.cfg;
  raceBodies.length = 0;
  for (const r of racers) {
    // cheap reject on the step state (no snapshot allocation far away)
    const bc = toGoalCoords(f, r.ball.state.x, r.ball.state.z);
    if (bc.s > cfg.depthTop - 4 && bc.s < cfg.depth + cfg.maxStretch + 4) {
      const b = r.ball.snapshot(alpha);
      const rad = r.ball.state.radius;
      raceBodies.push({
        key: r.ball,
        x: b.x,
        y: b.y,
        z: b.z,
        reach: rad,
        up: rad,
        coreLat: rad * 0.75,
        coreH: rad * 0.75,
        vInto: b.vx * f.fx + b.vz * f.fz,
      });
    }
    const cc = toGoalCoords(f, r.car.state.x, r.car.state.z);
    if (cc.s > cfg.depthTop - 6 && cc.s < cfg.depth + cfg.maxStretch + 4) {
      const k = r.car.snapshot(alpha);
      const cosA = forwardX(k.heading) * f.fx + forwardZ(k.heading) * f.fz;
      const box = vehicleBoxFor(r.vehicleId);
      raceBodies.push({
        key: r.car,
        x: k.x,
        y: k.y + 0.7,
        z: k.z,
        reach: carReach(cosA, box.halfLength, box.halfWidth),
        up: 0.7,
        coreLat: carReach(Math.sqrt(Math.max(0, 1 - cosA * cosA)), box.halfLength, box.halfWidth) * 0.85,
        coreH: 0.7,
        vInto: k.speed * Math.max(0, cosA),
      });
    }
  }
  net.press(raceBodies, performance.now() / 1000, still);
}

/** Smooth fall-off outside a body's footprint (1 inside it). */
function skirt(dLat: number, dH: number, coreLat: number, coreH: number, spread: number): number {
  const a = Math.max(0, Math.abs(dLat) - coreLat);
  const b = Math.max(0, Math.abs(dH) - coreH);
  return Math.exp(-(a * a + b * b) / (spread * spread));
}

/** A goal's net: bulges around whatever presses into it and ripples on hits. */
export class GoalNet {
  /** Called on each fresh hit (the race net routes it to the swish). */
  onHit: ((intensity: number) => void) | null = null;
  private presses: Press[] = [];
  private ripples: Ripple[] = [];
  private lastStretch = new Map<object, number>(); // per body: the hit edge
  private seen = new Set<object>();

  constructor(
    readonly frame: GoalFrame,
    readonly cfg: GoalCfg,
    private readonly panels: Panel[],
    /** This goal's size relative to the race goal (the clip's is smaller). */
    private readonly k: number,
  ) {}

  /** Press this frame's bodies into the net. `now` is the clock ripples run
   *  on (real seconds in a race; the clip passes its slow-motion world clock). */
  press(bodies: readonly GoalNetBody[], now: number, still: boolean): void {
    const f = this.frame;
    this.presses.length = 0;
    this.seen.clear();
    for (const b of bodies) {
      const c = toGoalCoords(f, b.x, b.z);
      const h = b.y - f.y;
      this.presses.push({ s: c.s, lat: c.lat, h, reach: b.reach, up: b.up, coreLat: b.coreLat, coreH: b.coreH });
      this.hit(b.key, c.s + b.reach - netDepthAt(h, this.cfg), c.lat, h, b.vInto, now, still);
      this.seen.add(b.key);
    }
    for (const key of this.lastStretch.keys()) if (!this.seen.has(key)) this.lastStretch.delete(key);
    while (this.ripples.length && (still || now - this.ripples[0].t0 > RIPPLE_LIFE)) this.ripples.shift();
    for (const p of this.panels) this.deform(p, now);
  }

  /** Forget ripples + hit edges (a clip replay). */
  reset(): void {
    this.ripples.length = 0;
    this.lastStretch.clear();
    this.presses.length = 0;
    for (const p of this.panels) this.deform(p, 0);
  }

  /** A body first biting into the back net: swish + a ripple. */
  private hit(key: object, stretch: number, lat: number, h: number, vInto: number, now: number, still: boolean): void {
    const last = this.lastStretch.get(key) ?? -1;
    this.lastStretch.set(key, stretch);
    const edge = 0.25 * this.k;
    if (!(stretch > edge && last <= edge)) return;
    const strength = Math.min(1.2, Math.max(0.3, vInto / (40 * this.k)));
    if (this === activeNet) onSwish?.(Math.min(1, strength));
    this.onHit?.(Math.min(1, strength));
    if (still) return;
    if (this.ripples.length >= RIPPLE_MAX) this.ripples.shift();
    this.ripples.push({ lat, h, t0: now, amp: 0.75 * strength * this.k });
  }

  private deform(p: Panel, now: number): void {
    const presses = this.presses;
    const ripples = p.kind === 'back' ? this.ripples : [];
    if (presses.length === 0 && ripples.length === 0 && !p.bulged) return;
    const f = this.frame;
    const cfg = this.cfg;
    const k = this.k;
    const spread = SKIRT * k;
    const wrap = WRAP * k;
    const n = p.rest.length / 3;
    const arr = p.pos.array as Float32Array;
    let any = false;
    for (let i = 0; i < n; i++) {
      const s = p.rest[i * 3];
      const lat = p.rest[i * 3 + 1];
      const h = p.rest[i * 3 + 2];
      let d = 0;
      for (const q of presses) {
        let amp: number;
        let fall: number;
        if (p.kind === 'back') {
          // the slanted net moves along its normal: scale so its HORIZONTAL
          // give matches how far the body has pushed past its rest depth
          amp = (q.s + q.reach - netDepthAt(q.h, cfg) + wrap) / Math.max(0.3, p.normal[i * 3]);
          if (amp <= 0) continue;
          fall = skirt(lat - q.lat, h - q.h, q.coreLat, q.coreH, spread);
        } else {
          amp = q.h + q.up - cfg.height + wrap;
          if (amp <= 0 || q.s < 0) continue;
          fall = skirt(lat - q.lat, s - q.s, q.coreLat, q.coreLat, spread);
        }
        const v = amp * fall;
        if (v > d) d = v;
      }
      for (const w of ripples) {
        const age = now - w.t0;
        const dl = lat - w.lat;
        const dh = h - w.h;
        const rr = Math.sqrt(dl * dl + dh * dh) / k;
        const front = Math.min(1, Math.max(0, (RIPPLE_FRONT * age + 1.5 - rr) / 2));
        if (front <= 0) continue;
        d += w.amp * front * Math.exp(-age * RIPPLE_DECAY - rr * 0.1) * Math.sin(RIPPLE_K * rr - RIPPLE_W * age);
      }
      d *= p.pin[i];
      if (Math.abs(d) > 1e-3) any = true;
      const ss = s + p.normal[i * 3] * d;
      const ll = lat + p.normal[i * 3 + 1] * d;
      const hh = h + p.normal[i * 3 + 2] * d;
      arr[i * 3] = f.x + f.fx * ss + f.rx * ll;
      arr[i * 3 + 1] = f.y + hh;
      arr[i * 3 + 2] = f.z + f.fz * ss + f.rz * ll;
    }
    p.pos.needsUpdate = true;
    p.bulged = any;
  }
}

// ---- painters -------------------------------------------------------------

/** One tile of HEXAGONAL netting (the stadium net) on a clear ground. Flat-top
 *  hexagons tile with a period of 3a across and √3·a up, so a canvas of that
 *  aspect repeats seamlessly; NET_HEX_W / NET_HEX_H are the tile in world
 *  units. A square mesh read as graph paper from inside the goal (measured). */
function netTexture(): THREE.CanvasTexture {
  const W = 256;
  const H = Math.round(W / Math.sqrt(3)); // 148
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const x = c.getContext('2d');
  if (!x) throw new Error('finishGoal: no 2D context');
  x.clearRect(0, 0, W, H);
  const a = W / 3;
  const ay = H / Math.sqrt(3); // = a, rounded to the canvas
  const hex = (cx: number, cy: number): void => {
    x.beginPath();
    for (let k = 0; k <= 6; k++) {
      const t = (Math.PI / 3) * k;
      const px = cx + a * Math.cos(t);
      const py = cy + ay * Math.sin(t);
      if (k === 0) x.moveTo(px, py);
      else x.lineTo(px, py);
    }
    x.stroke();
  };
  const centres: [number, number][] = [];
  for (const cx of [0, W]) for (const cy of [0, H]) centres.push([cx, cy]);
  for (const cy of [-H / 2, H / 2, (3 * H) / 2]) centres.push([W / 2, cy]);
  x.lineJoin = 'round';
  // the cord, then a thin darker core so it reads as rope, not paint
  x.strokeStyle = 'rgba(255,255,255,0.95)';
  x.lineWidth = 9;
  for (const [cx, cy] of centres) hex(cx, cy);
  x.strokeStyle = 'rgba(196,203,212,0.85)';
  x.lineWidth = 2.5;
  for (const [cx, cy] of centres) hex(cx, cy);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  return tex;
}

/** The board behind the net: near-black, gold rules, the brand three times. */
function paintBoard(c: HTMLCanvasElement, logo: HTMLImageElement | null): void {
  const x = c.getContext('2d');
  if (!x) return;
  const W = c.width;
  const H = c.height;
  const bg = x.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#16171c');
  bg.addColorStop(1, '#08090c');
  x.fillStyle = bg;
  x.fillRect(0, 0, W, H);
  const gold = x.createLinearGradient(0, 0, W, 0);
  gold.addColorStop(0, '#8a5d12');
  gold.addColorStop(0.5, '#f2c25a');
  gold.addColorStop(1, '#8a5d12');
  x.fillStyle = gold;
  const rule = Math.round(H * 0.07);
  x.fillRect(0, rule, W, rule);
  x.fillRect(0, H - 2 * rule, W, rule);
  const slots = 3;
  for (let i = 0; i < slots; i++) {
    const cx = (W * (i + 0.5)) / slots;
    if (logo) {
      const h = H * 0.7;
      const w = (h * logo.naturalWidth) / logo.naturalHeight;
      x.drawImage(logo, cx - w / 2, H / 2 - h / 2, w, h);
    } else {
      x.save();
      x.textAlign = 'center';
      x.textBaseline = 'middle';
      x.font = `900 ${Math.round(H * 0.42)}px "Arial Black", Arial, sans-serif`;
      x.fillStyle = '#d9dee6';
      x.fillText('DRIBBLE', cx - H * 0.95, H / 2);
      x.fillStyle = '#f2b21c';
      x.fillText('RACER', cx + H * 1.35, H / 2);
      x.restore();
    }
  }
}

function boardTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  // the canvas keeps the board's own aspect (~35 × BOARD_H) so the logo
  // art is never squashed
  c.width = 2048;
  c.height = Math.round((2048 * BOARD_H) / 35);
  paintBoard(c, null);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  withBrandLogo((img) => {
    paintBoard(c, img);
    t.needsUpdate = true;
  });
  return t;
}

// ---- geometry -------------------------------------------------------------

/** A round tube between two world points. */
function tube(a: THREE.Vector3, b: THREE.Vector3, radius: number, seg = 14): THREE.BufferGeometry {
  const len = a.distanceTo(b);
  const g = new THREE.CylinderGeometry(radius, radius, len, seg, 1, false);
  const mid = a.clone().add(b).multiplyScalar(0.5);
  const dir = b.clone().sub(a).normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  g.applyMatrix4(new THREE.Matrix4().compose(mid, q, new THREE.Vector3(1, 1, 1)));
  return g;
}

/** Where and how big a goal is built. */
export interface GoalSpec {
  frame: GoalFrame;
  /** Dimensions + net (tuning.goal for a race; a scaled copy in the clip). */
  cfg: GoalCfg;
  /** Half the mouth: post centres sit at ±(halfWidth + postRadius). */
  halfWidth: number;
  /** The turf's height under a depth into the goal, relative to the line. */
  turf: (s: number) => number;
  /** The goal's side line (post centre line) at a depth — follows the real
   *  rails where the road keeps curving through the goal. */
  edge: (s: number, side: -1 | 1) => number;
  /** Where the side nets start: the guardrail's top. */
  railTop: number;
}

/** Build a goal + its net from a spec. Does NOT register the race net. */
export function buildGoal(spec: GoalSpec): { group: THREE.Group; net: GoalNet } {
  const group = new THREE.Group();
  const { frame: f, cfg: G, turf, edge, railTop: RAIL_TOP } = spec;
  /** Size relative to the race goal: every visual dimension scales by it. */
  const k = G.height / tuning.goal.height;
  const hw = spec.halfWidth;
  const pr = G.postRadius;
  const L = hw + pr; // post centres
  const barH = G.height + pr; // crossbar centre
  const at = (s: number, lat: number, h: number): THREE.Vector3 =>
    new THREE.Vector3(f.x + f.fx * s + f.rx * lat, f.y + h, f.z + f.fz * s + f.rz * lat);
  const across = (s: number, u: number): number => edge(s, -1) + (edge(s, 1) - edge(s, -1)) * u;

  // a whisper of self-light so the frame still reads WHITE in the night
  // cities (Tokyo's posts read gunmetal under its lights — reviewed)
  const white = new THREE.MeshStandardMaterial({
    color: 0xf2f4f7,
    roughness: 0.3,
    metalness: 0.12,
    emissive: 0xc9cdd3,
    emissiveIntensity: 0.22,
  });
  const gold = new THREE.MeshStandardMaterial({ color: 0xd8a63a, roughness: 0.35, metalness: 0.85 });
  const whiteGeos: THREE.BufferGeometry[] = [];
  const goldGeos: THREE.BufferGeometry[] = [];

  // posts + crossbar (rounded corners where they meet)
  for (const side of [-1, 1]) {
    whiteGeos.push(tube(at(0, side * L, turf(0) - 0.2), at(0, side * L, barH), pr, 18));
    const knee = new THREE.SphereGeometry(pr, 18, 10);
    knee.translate(at(0, side * L, barH).x, at(0, side * L, barH).y, at(0, side * L, barH).z);
    whiteGeos.push(knee);
    // gold collar just above the guardrail — the rail hides the post's true
    // foot, so a collar AT the foot was a stray gold sliver (reviewed); a
    // slim rim keeps it off the road side of the rail
    const collar = new THREE.CylinderGeometry(pr * 1.28, pr * 1.28, 0.75 * k, 20);
    const foot = at(0, side * L, turf(0) + RAIL_TOP + 0.5 * k);
    collar.translate(foot.x, foot.y, foot.z);
    goldGeos.push(collar);
    // and a thin gold band where each post meets the crossbar
    const band = new THREE.CylinderGeometry(pr * 1.18, pr * 1.18, 0.32 * k, 20);
    const top = at(0, side * L, barH - 1.0 * k);
    band.translate(top.x, top.y, top.z);
    goldGeos.push(band);
  }
  whiteGeos.push(tube(at(0, -L, barH), at(0, L, barH), pr, 18));

  // the frame behind: top bars, stanchions, back bars
  const sr = 0.2 * k;
  const footY = turf(G.depth);
  for (const side of [-1, 1] as const) {
    const topLat = edge(G.depthTop, side);
    const footLat = edge(G.depth, side);
    whiteGeos.push(tube(at(0, side * L, barH), at(G.depthTop, topLat, barH), sr));
    whiteGeos.push(tube(at(G.depthTop, topLat, barH), at(G.depth, footLat, footY + sr), sr));
    const knee = new THREE.SphereGeometry(sr, 12, 8);
    const kp = at(G.depthTop, topLat, barH);
    knee.translate(kp.x, kp.y, kp.z);
    whiteGeos.push(knee);
    const cap = new THREE.CylinderGeometry(0.34 * k, 0.4 * k, 0.5 * k, 14);
    const cf = at(G.depth, footLat, footY + 0.25 * k);
    cap.translate(cf.x, cf.y, cf.z);
    goldGeos.push(cap);
  }
  const bar = 0.16 * k;
  whiteGeos.push(tube(at(G.depthTop, edge(G.depthTop, -1), barH), at(G.depthTop, edge(G.depthTop, 1), barH), bar));
  whiteGeos.push(tube(at(G.depth, edge(G.depth, -1), footY + bar), at(G.depth, edge(G.depth, 1), footY + bar), bar));

  const frameWhite = mergeGeometries(whiteGeos, false);
  const frameGold = mergeGeometries(goldGeos, false);
  for (const g of [...whiteGeos, ...goldGeos]) g.dispose();
  if (frameWhite) group.add(new THREE.Mesh(frameWhite, white));
  if (frameGold) group.add(new THREE.Mesh(frameGold, gold));

  // ---- the net -------------------------------------------------------------
  const netTex = netTexture();
  const netMat = new THREE.MeshBasicMaterial({
    map: netTex,
    color: 0xeef1f5,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    alphaTest: 0.02,
  });
  // Far away the mip chain averages the thin cords into a flat white VEIL
  // (reviewed: "a hazy screen", merging with the Maracanã's facade). Bending
  // alpha by a power keeps the near cords (alpha ~1) solid while the
  // averaged far veil (alpha ~0.15-0.3) thins out fast — like a real net,
  // which all but vanishes from up the road.
  netMat.onBeforeCompile = (shader): void => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      '#include <map_fragment>\n\tdiffuseColor.a = pow( diffuseColor.a, NET_ALPHA_POW );',
    );
    shader.fragmentShader = `#define NET_ALPHA_POW ${NET_ALPHA_POW.toFixed(2)}\n${shader.fragmentShader}`;
  };
  const panels: Panel[] = [];

  /** A grid panel from a (u,v) → goal-coords mapping; `uvScale` → texture. */
  const panel = (
    cols: number,
    rows: number,
    map: (u: number, v: number) => { s: number; lat: number; h: number; tu: number; tv: number },
    normal: (u: number, v: number) => [number, number, number],
    pin: (u: number, v: number) => number,
    kind: Panel['kind'] | null,
  ): void => {
    const nv = (cols + 1) * (rows + 1);
    const pos = new Float32Array(nv * 3);
    const uv = new Float32Array(nv * 2);
    const rest = new Float32Array(nv * 3);
    const nrm = new Float32Array(nv * 3);
    const pins = new Float32Array(nv);
    let i = 0;
    for (let rI = 0; rI <= rows; rI++) {
      for (let cI = 0; cI <= cols; cI++) {
        const u = cI / cols;
        const v = rI / rows;
        const m = map(u, v);
        const w = at(m.s, m.lat, m.h);
        pos.set([w.x, w.y, w.z], i * 3);
        rest.set([m.s, m.lat, m.h], i * 3);
        nrm.set(normal(u, v), i * 3);
        pins[i] = pin(u, v);
        uv.set([m.tu / (NET_HEX_W * k), m.tv / (NET_HEX_H * k)], i * 2);
        i++;
      }
    }
    const idx: number[] = [];
    for (let rI = 0; rI < rows; rI++) {
      for (let cI = 0; cI < cols; cI++) {
        const a = rI * (cols + 1) + cI;
        const b = a + 1;
        const c = a + cols + 1;
        const d = c + 1;
        idx.push(a, c, b, b, c, d);
      }
    }
    const geo = new THREE.BufferGeometry();
    const posAttr = new THREE.BufferAttribute(pos, 3);
    posAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', posAttr);
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setIndex(idx);
    const mesh = new THREE.Mesh(geo, netMat);
    mesh.frustumCulled = false; // it bulges past its rest bounds
    mesh.renderOrder = 2; // after the opaque world, before the HUD-ish sprites
    group.add(mesh);
    if (kind) panels.push({ mesh, pos: posAttr, rest, normal: nrm, pin: pins, kind, bulged: true });
  };

  // back net: slanted from the roof's far edge down to the deep turf line
  const backDs = G.depth - G.depthTop;
  const backDh = barH - footY;
  const backLen = Math.hypot(backDs, backDh);
  const bn: [number, number, number] = [backDh / backLen, 0, backDs / backLen];
  panel(
    Math.ceil((2 * L) / (NET_GRID * k)),
    Math.ceil(backLen / (NET_GRID * k)),
    (u, v) => {
      const sRow = G.depth - backDs * v;
      const lat = across(sRow, u);
      const slack = BACK_SLACK * k * Math.sin(Math.PI * u) * Math.sin(Math.PI * v);
      return {
        s: sRow + bn[0] * slack,
        lat,
        h: footY + backDh * v + bn[2] * slack,
        tu: lat,
        tv: backLen * v,
      };
    },
    () => bn,
    (_u, v) => 0.4 + 0.6 * Math.min(1, (v * backLen) / (1.6 * k)),
    'back',
  );
  // roof net: crossbar to the back top bar, sagging between them
  panel(
    Math.ceil((2 * L) / (NET_GRID * k)),
    Math.ceil(G.depthTop / (NET_GRID * k)),
    (u, v) => {
      const s = pr + (G.depthTop - pr) * v;
      const lat = across(s, u);
      return {
        s,
        lat,
        h: barH - ROOF_SAG * k * Math.sin(Math.PI * u) * Math.sin(Math.PI * v),
        tu: lat,
        tv: s,
      };
    },
    () => [0, 0, 1],
    () => 1,
    'roof',
  );
  // side nets: from the rail top to the roof, from the post back to the slant
  for (const side of [-1, 1] as const) {
    panel(
      Math.ceil(G.depth / (0.8 * k)),
      8,
      (u, v) => {
        const hTop = barH;
        const sBack = (h: number): number => G.depth - backDs * Math.min(1, Math.max(0, (h - footY) / backDh));
        const h0 = RAIL_TOP; // relative to the local turf
        const hRel = h0 + (hTop - h0) * v;
        const s = u * sBack(hRel);
        return { s, lat: edge(s, side), h: hRel + turf(s) * (1 - v), tu: s, tv: hRel };
      },
      () => [0, side, 0],
      () => 1,
      null,
    );
  }

  // ---- the board behind the net -------------------------------------------
  {
    const s = G.depth + G.maxStretch + BOARD_GAP * k;
    const boardH = BOARD_H * k;
    const base = turf(s);
    const face = new THREE.MeshBasicMaterial({ map: boardTexture() });
    const dark = new THREE.MeshStandardMaterial({ color: 0x0c0d10, roughness: 0.6, metalness: 0.3 });
    // BoxGeometry faces: [+x, −x, +y, −y, +z, −z]; local −z faces the road
    const lo = edge(s, -1);
    const hi = edge(s, 1);
    const board = new THREE.Mesh(new THREE.BoxGeometry(hi - lo - 2 * pr - 1 * k, boardH, 0.3 * k), [
      dark,
      dark,
      dark,
      dark,
      dark,
      face,
    ]);
    const c = at(s, (lo + hi) / 2, base + 0.15 * k + boardH / 2);
    board.position.copy(c);
    board.rotation.y = Math.atan2(f.fx, f.fz); // local +Z = into the goal
    group.add(board);
    // feet
    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.25 * k, 0.4 * k, 0.9 * k), dark);
      leg.position.copy(at(s + 0.2 * k, (lo + hi) / 2 + side * (hw - 3 * k), base + 0.2 * k));
      leg.rotation.y = board.rotation.y;
      group.add(leg);
    }
  }

  return { group, net: new GoalNet(f, G, panels, k) };
}

/** Build the race goal for this track and register it as the live race net. */
export function buildFinishGoal(track: Track): THREE.Group {
  const f = track.goalFrame;
  if (!f) return new THREE.Group();
  const fin = track.finishDistance;
  const L = track.halfWidth + tuning.goal.postRadius;
  const { group, net } = buildGoal({
    frame: f,
    cfg: tuning.goal,
    halfWidth: track.halfWidth,
    /** The road's height under a depth into the goal, relative to the line. */
    turf: (s) => track.sampleAt(Math.min(track.length, fin + s)).y - f.y,
    /** The goal's side line at a depth: the real rail (+ a post radius), so a
     *  road that keeps curving through the goal keeps its sides inside the
     *  nets (posts sit exactly at ±L; the box skews by under a unit). */
    edge: (s, side) => {
      const smp = track.sampleAt(Math.min(track.length, fin + Math.max(0, s)));
      return toGoalCoords(f, smp.x + smp.rightX * side * L, smp.z + smp.rightZ * side * L).lat;
    },
    railTop: RAIL_TOP,
  });
  activeNet = net;
  return group;
}
